import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
	meiliSearchIndex,
	useMeiliSearch,
	useMeiliSearchKey,
} from '@uturn/portal/meilisearch';
import { getApiErrorMessage } from '@uturn/portal/utils';

export const useFacetSearch = (
	facetName: string,
	groupId?: string,
	extraFilters: string[] = [],
) => {
	const meiliSearch = useMeiliSearch();
	const queryClient = useQueryClient();
	const queryKey = useMeiliSearchKey();

	const filters = [
		...(groupId ? [`groupId = ${groupId}`] : []),
		...extraFilters,
	];
	const filter = filters.length > 0 ? filters.join(' AND ') : undefined;

	return useQuery({
		queryKey: ['facet-search', facetName, groupId, ...extraFilters],
		queryFn: async () => {
			if (!meiliSearch) {
				return null;
			}

			try {
				await meiliSearch.health();
			} catch (error) {
				const message = getApiErrorMessage(error);
				if (message?.includes('Authorization') || message?.includes('token')) {
					queryClient.invalidateQueries({ queryKey });
				}
				throw error;
			}

			const shipmentsIndex = meiliSearchIndex(meiliSearch);
			try {
				const result = await shipmentsIndex.searchForFacetValues({
					facetName,
					filter,
				});
				return result;
			} catch (error) {
				const message = getApiErrorMessage(error);

				if (message?.includes('Not Found')) {
					Sentry.captureMessage(
						`MeiliSearch index not found: ${message}`,
						'warning',
					);
					return null;
				}

				if (
					message?.includes('Authorization') ||
					message?.includes('token expired')
				) {
					queryClient.invalidateQueries({ queryKey });
					return null;
				}

				Sentry.captureException(error, {
					tags: { context: 'useFacetSearch' },
				});
				return null;
			}
		},
		enabled: !!meiliSearch,
		retry: (failureCount, error) => {
			const message = getApiErrorMessage(error);
			if (
				message?.includes('Authorization') ||
				message?.includes('token expired') ||
				message?.includes('Not Found')
			) {
				return failureCount < 2;
			}
			return failureCount < 3;
		},
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
	});
};
